import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStatus } from "./session";
import { AuthSplash } from "./AuthSplash";
import { CHECK_EMAIL_PATH, LOGIN_PATH } from "./paths";
import { AUTH_ENABLED } from "./config";

/* Hard gate for the dashboard. When auth is disabled (the default everywhere
 * except gated deployments) it renders children straight through — and, since
 * AuthProvider hasn't mounted the session provider in that case, it must NOT
 * call the session hook, so the gated logic lives in a separate component
 * below.
 *
 * When enabled, unauthenticated users are redirected to the dedicated /login
 * route (rendered by LoginRoute) rather than rendering the login screen inline,
 * so the URL reflects the auth state. */
export function RequireAuth({ children }: { children: ReactNode }) {
  if (!AUTH_ENABLED) return <>{children}</>;

  return <AuthGate>{children}</AuthGate>;
}

function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useAuthStatus();
  const location = useLocation();

  if (status === "loading") return <AuthSplash />;

  // Signed in but unverified — route to the check-email screen (they have a
  // session; they just need to verify), carrying where they were headed in a
  // `redirect` param so e.g. a /invite/:id intent survives verification
  // (CheckEmailRoute forwards there once verified).
  if (status === "unverified") {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${CHECK_EMAIL_PATH}?redirect=${redirect}`} replace />;
  }

  // Anything that isn't "authenticated" (anonymous, error) means no session.
  // Send them to /login with where they were headed in a `redirect` query param
  // (full path + search), so LoginRoute can return them there after login. A
  // URL param (not router state) survives a manual refresh of /login and the
  // social-login full-page redirect round-trip. Any ?invitation_id=… rides
  // along inside the captured search, so the invite deeplink is preserved.
  if (status !== "authenticated") {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${LOGIN_PATH}?redirect=${redirect}`} replace />;
  }

  return <>{children}</>;
}
